import {
  GridColTypeDef,
  GridFilterInputValue,
  GridFilterOperator,
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators
} from '@mui/x-data-grid-pro'

import { ListStructure } from './use-list-service/types'
import { BooleanValue, LicensePlate, LocalizedDate, LocalizedDateTime, TranslatedEnumerable } from './data-grid-cell-renderers'

import { DataGridFilterSingleSelect } from './DataGridFilterSingleSelect'

const stringFilterOperators: GridFilterOperator[] = [
  {
    value: 'contains',
    InputComponent: GridFilterInputValue,
    requiresFilterValue: true,
    getApplyFilterFn() {
      return null
    }
  }
]

const allowedNumberOperators = ['=', '!=', '>', '<']

const numberFilterOperators: GridFilterOperator[] = getGridNumericOperators()
  .filter(operator => allowedNumberOperators.includes(operator.value))
  .map(operator => ({
    ...operator,
    getApplyFilterFn() {
      return null
    }
  }))

const enumerableFilterOperators: GridFilterOperator[] = [
  {
    InputComponent: DataGridFilterSingleSelect,
    requiresFilterValue: true,
    value: 'is',
    getApplyFilterFn() {
      return null
    }
  },
  {
    InputComponent: DataGridFilterSingleSelect,
    requiresFilterValue: true,
    value: 'not',
    getApplyFilterFn() {
      return null
    }
  }
]

const allowedDateOperators = ['is', 'not', 'after', 'before', 'onOrAfter', 'onOrBefore']

const dateFilterOperators: GridFilterOperator[] = getGridDateOperators()
  .filter(operator => allowedDateOperators.includes(operator.value))
  .map(operator => ({
    ...operator,
    getApplyFilterFn() {
      return null
    },
    InputComponentProps: {
      ...operator.InputComponentProps,
      sx: {
        // This styling is needed so the built in datepicker's placeholder
        // doesn't overlap with MUI's textfield label.
        '& input[value=""]:not(:focus)': {
          color: 'transparent'
        }
      }
    }
  }))

const booleanFilterOperators: GridFilterOperator[] = getGridBooleanOperators().map(operator => ({
  ...operator,
  getApplyFilterFn() {
    return null
  }
}))

const columnDefinitions = {
  boolean: {
    filterable: true,
    filterOperators: booleanFilterOperators,
    renderCell: params => <BooleanValue>{params.value}</BooleanValue>
  },
  date: {
    filterable: true,
    filterOperators: dateFilterOperators,
    renderCell: params => <LocalizedDate>{params.value}</LocalizedDate>
  },
  dateTime: {
    filterable: true,
    filterOperators: dateFilterOperators,
    renderCell: params => <LocalizedDateTime>{params.value}</LocalizedDateTime>
  },
  default: {
    filterable: false
  },
  emailAddress: {
    filterable: true,
    filterOperators: stringFilterOperators
  },
  enumerable: {
    filterable: true,
    filterOperators: enumerableFilterOperators,
    // According to the types colDef.type can be undefined.
    // This is not true it'll always at least be "string".
    // That is why the non null assertion operator (!) is used here.
    renderCell: params => <TranslatedEnumerable type={params.colDef.type!} value={params.value} />
  },
  number: {
    filterable: true,
    filterOperators: numberFilterOperators
  },
  registration: {
    filterable: true,
    filterOperators: stringFilterOperators,
    renderCell: params => <LicensePlate>{params.value}</LicensePlate>
  },
  string: {
    filterable: true,
    filterOperators: stringFilterOperators
  }
} satisfies Record<string, GridColTypeDef>

type ColumnTypeLookupTable = Record<string, string>

export function mapStructureToColumnTypeLookupTable(structure: ListStructure): ColumnTypeLookupTable {
  return Object.fromEntries(
    Object.entries(structure).flatMap(([entityName, entity]) => {
      return Object.entries(entity).map(([propertyName, propertyType]) => [`${entityName}.${propertyName}`, propertyType])
    })
  )
}

function queryColumnTypeByColumnField(structure: ColumnTypeLookupTable, columnField: string): string | undefined {
  return structure[columnField]
}

function isSliceOfEnumerable(columnType: string) {
  return columnType.startsWith('SliceOfEnum')
}

function isEnumerable(columnType: string) {
  return columnType.startsWith('Enum')
}

export function queryColumnDefinitionByColumnField(structure: ColumnTypeLookupTable, columnField: string): GridColTypeDef {
  const columnType = queryColumnTypeByColumnField(structure, columnField)

  if (!columnType) return columnDefinitions.default

  if (isEnumerable(columnType) || isSliceOfEnumerable(columnType)) {
    // Add the enum type as the column type.
    // This will be used to determine the translation string
    // and select the correct options for the filter.
    return {
      type: columnType,
      ...columnDefinitions.enumerable,
      // Filtering on slices of enums doesn't work yet
      // so we're setting filterable to false for them.
      filterable: !isSliceOfEnumerable(columnType)
    }
  }

  switch (columnType) {
    case 'bool':
      return columnDefinitions.boolean
    case 'Date':
      return columnDefinitions.date
    case 'DateTime':
      return columnDefinitions.dateTime
    case 'EmailAddress':
      return columnDefinitions.emailAddress
    case 'Money':
    case 'int':
    case 'float32':
    case 'float64':
      return columnDefinitions.number
    case 'Registration':
      return columnDefinitions.registration
    case 'ID':
    case 'UUID':
    case 'VIN':
    case 'string':
      return columnDefinitions.string
    default:
      return columnDefinitions.default
  }
}
